import { InputComponent } from "../../../../Utils/UI/InputComponent/InputComponent";
import "./EmobilityStep.scss";
import { useContext, useEffect, useRef, useState } from "react";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { useOrder } from "../../../../../Contexts/OrderContext";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Box, FormControl } from "@mui/material";
import { NorlysRadioButton } from "../../../../Utils/UI/RadioButton/NorlysRadioButton";
import { IAddressAutocompleteResult } from "../../../../../Models/AddressAutocompleteModel";
import { AddressAutocomplete } from "../../../../SharedComponents/AddressAutocomplete/AddressAutocomplete";
import { toAddressRequestFromLookup } from "../../../../../Helpers/AddressHelpers";
import { fetchAddressById } from "../../../../../Services/AddressAutocompleteService";
import { InfoTooltip } from "../../../../Utils/UI/InfoBadge/InfoBadge";

export const EmobilityStep = () => {
	const { order, getOrder, updateOrder } = useOrder();
	const partnerContentContext = useContext(PartnerContentContext);
	const [months, setMonths] = useState<Array<Date>>([]);
	const [addressLookupPending, setAddressLookupPending] = useState(false);
	const [addressLookupError, setAddressLookupError] = useState(false);
	const selectedAddressId = useRef<string | null>(null);

	const danish = new Intl.Locale("da-DK");

	useEffect(() => {
		setMonths(generateMonths());
	}, []);

	function generateMonths(): Array<Date> {
		const nextTwelveMonths: Date[] = [];
		for (let i = 1; i < 13; i++) {
			let date = new Date();
			date.setDate(1);
			date.setMonth(date.getMonth() + i);
			nextTwelveMonths.push(date);
		}
		return nextTwelveMonths;
	}

	const handleCampaignCodeChange = (val: { value: string; valid: boolean; error: string }): any => {
		updateOrder({ emobilityCampaign: val.value });
	};

	const handleMonthChange = (event: SelectChangeEvent) => {
		let date = new Date(event.target.value);
		let datestring = getMonthstring(date);

		updateOrder({
			emobilityWishedInstallationMonthName: datestring,
			emobilityWishedInstallationMonth: date,
		});
	};

	const getMonthstring = (date: Date) => {
		return date.toLocaleString(danish, { month: "long" }) + " " + date.getFullYear();
	};

	function stringToBoolean(value: string): boolean {
		if (value.toLowerCase() === "true") return true;
		else return false;
	}

	const clearSeparateAddress = () => {
		selectedAddressId.current = null;
		setAddressLookupPending(false);
		setAddressLookupError(false);
		updateOrder({
			separateDeliveryAddressString: "",
			separateDeliveryAddressId: "",
			separateDeliveryAddressRequest: undefined,
		});
	};

	const handleAddressChange = (val: IAddressAutocompleteResult): any => {
		if (val.valid) {
			const addressId = val.value.adresse.id.toString();
			selectedAddressId.current = addressId;
			setAddressLookupPending(true);
			setAddressLookupError(false);
			updateOrder({
				separateDeliveryAddressString: val.value.tekst,
				separateDeliveryAddressId: addressId,
				separateDeliveryAddressRequest: undefined,
			});
			fetchAddressById(addressId)
				.then((details) => {
					if (
						selectedAddressId.current !== addressId ||
						getOrder().separateDeliveryAddressId !== addressId
					) {
						return;
					}

					const addressRequest = details?.id === addressId
						? toAddressRequestFromLookup(details)
						: null;
					setAddressLookupPending(false);
					if (!addressRequest) {
						setAddressLookupError(true);
						return;
					}

					setAddressLookupError(false);
					updateOrder({ separateDeliveryAddressRequest: addressRequest });
				})
				.catch(() => {
					if (selectedAddressId.current === addressId) {
						setAddressLookupPending(false);
						setAddressLookupError(true);
					}
				});
		} else {
			clearSeparateAddress();
		}
	};

	return (
		<div>
			<div className="emobility-wrapper">
				<div>
					<div className="month-select-title" style={{ fontWeight: "bold" }}>
						Ønsket installationsmåned
					</div>

					<FormControl fullWidth={true}>
						<Box width={600}>
							<Select
								fullWidth={true}
								onChange={handleMonthChange}
								sx={{ backgroundColor: "white" }}>
								{months.map((month) => {
									return (
										<MenuItem key={month.toString()} value={month.toString()}>
											{getMonthstring(month)}
										</MenuItem>
									);
								})}
							</Select>
						</Box>
					</FormControl>

					<Box sx={{ display: "flex", flexDirection: "column", marginLeft: "0.2rem" }}>
						<NorlysRadioButton
							id={"emobilityRemovalOfChargingStand"}
							title={"Har du allerede en ladeboks installeret på adressen?"}
							onChange={(event) => {
								updateOrder({
									emobilityRemovalOfChargingStand: stringToBoolean(event.target.value),
								});
							}}
							values={[
								{ label: "Nej", value: false },
								{ label: "Ja", value: true },
							]}
							chosenValue={order.emobilityRemovalOfChargingStand ?? false}>
							<InfoTooltip
								placement="top"
								paddingRight={0.406}
								message="Hvis du i forvejen har en ladeboks, kan du spare penge på installationen. Kablet fra din eltavle til ladeboksen skal have en minimumstykkelse på 5 &lowast; 4 mm&#178; (til 11kW ladeboks) og den må ikke forsyne andet end ladeboksen. For at få rabat på installationen, skal du have en elaftale hos Norlys."
							/>
						</NorlysRadioButton>

						<NorlysRadioButton
							id={"separateDeliveryAddress"}
							title={
								"Ladeboksen bliver sendt direkte til dig. Skal den leveres til en anden adresse end installationsadressen?"
							}
							onChange={(event) => {
								const separateDeliveryAddress = stringToBoolean(event.target.value);
								updateOrder({ separateDeliveryAddress });
								if (!separateDeliveryAddress) clearSeparateAddress();
							}}
							values={[
								{ label: "Nej", value: false },
								{ label: "Ja", value: true },
							]}
							chosenValue={order.separateDeliveryAddress ?? false}
						/>

						{order.separateDeliveryAddress && (
							<Box sx={{ marginTop: "1rem;" }}>
								<AddressAutocomplete
									placeholder={"Alternativ leveringsadresse"}
									validAddress={(isValid) => {
										if (!isValid) clearSeparateAddress();
										return isValid;
									}}
									change={handleAddressChange}
								/>
								{addressLookupPending && <p>Henter adresseoplysninger...</p>}
								{addressLookupError && (
									<p role="alert">Kunne ikke hente adresseoplysninger. Vælg adressen igen.</p>
								)}
							</Box>
						)}

						{partnerContentContext.financingEnabled && (
							<NorlysRadioButton
								id={"emobilityDealerFunded"}
								title={
									"Hvordan skal engangsudgiften for ladeboks og standard installation betales?"
								}
								onChange={(event) => {
									updateOrder({
										emobilityDealerFunded: stringToBoolean(event.target.value),
									});
								}}
								values={[
									{ label: "På min regning fra Norlys", value: false },
									{ label: "Afregnes til partner", value: true },
								]}
								chosenValue={order.emobilityDealerFunded ?? false}
							/>
						)}
						
						{partnerContentContext.campaignCodeEnabled && (
							<Box marginTop={"1rem"}>
								<InputComponent
									width={548}
									placeholder="Kampagnekode"
								validationType="none"
									change={(e): any => {
										handleCampaignCodeChange(e);
									}}
								/>
							</Box>
						)}
					</Box>
				</div>
			</div>
		</div>
	);
};
